import CharacterCount from '@tiptap/extension-character-count';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Editor as TiptapEditor, Extension, mergeAttributes, Node } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ForwardedRef,
} from 'react';

import ToolbarPlugin, { type EquationEditTarget } from './plugins/ToolbarPlugin';
import { lineHeights } from './plugins/ToolbarContext';

const placeholder = 'Enter text...';

export function parseAllowedColor(input: string) {
  const element = document.createElement('span');
  element.style.color = input;
  const normalized = element.style.color;
  return normalized.startsWith('rgb(') || normalized.startsWith('rgba(') ? normalized : '';
}

export function parseAllowedLineHeight(input: string) {
  return lineHeights.includes(input as typeof lineHeights[number]) ? input : '';
}

const LineHeight = Extension.create({
  name: 'lineHeight',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph'],
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: element => parseAllowedLineHeight(element.style.lineHeight) || null,
            renderHTML: attributes => {
              const value = parseAllowedLineHeight(attributes.lineHeight);
              return value && value !== 'default' ? { style: `line-height: ${value}` } : {};
            },
          },
        },
      },
    ];
  },
});

export const Equation = Node.create({
  name: 'equation',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      latex: { default: '' },
      svg: { default: '' },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img.equation[data-latex]',
        getAttrs: element => {
          if (!(element instanceof HTMLImageElement)) return false;
          return {
            latex: element.dataset.latex || '',
            svg: element.src.replace(/^data:image\/svg\+xml;utf8,/, ''),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { latex, svg, ...attributes } = HTMLAttributes;
    return [
      'img',
      mergeAttributes(attributes, {
        class: 'equation',
        src: `data:image/svg+xml;utf8,${svg}`,
        'data-latex': latex,
      }),
    ];
  },
});

function useDebouncedAutosave(callback: (content: string) => void, delay: number) {
  const callbackRef = useRef(callback);
  const timerRef = useRef<number>();

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  return (content: string) => {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => callbackRef.current(content), delay);
  };
}

export type EditorHandle = {
  getCurrentContent: () => string | undefined;
};

type Params = {
  onChange: (content: string) => void;
  onAutosave: (content: string) => void;
  type: 'none' | 'basic';
  showEquationEditorButton: boolean;
  equationEditorContainerElement?: HTMLElement;
  initialData?: string;
  id: string;
};

function wordCount(editor: TiptapEditor | null) {
  return editor?.storage.characterCount.words() || 0;
}

function serializeEditor(editor: TiptapEditor) {
  const container = editor.view.dom.cloneNode(true) as HTMLElement;
  container.querySelectorAll('.ProseMirror-trailingBreak').forEach(node => node.remove());
  container.removeAttribute('contenteditable');
  container.removeAttribute('translate');
  container.removeAttribute('tabindex');
  container.removeAttribute('role');
  container.removeAttribute('aria-label');
  container.removeAttribute('class');
  return container.innerHTML;
}

export default forwardRef(function Editor(
  props: Params,
  handleRef: ForwardedRef<EditorHandle>,
) {
  const autosave = useDebouncedAutosave(props.onAutosave, 1000);
  const [equationEditTarget, setEquationEditTarget] = useState<EquationEditTarget | null>(null);
  const [equationEditorIsOpen, setEquationEditorIsOpen] = useState(false);
  const [, rerenderToolbar] = useState(0);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [editor, setEditor] = useState<TiptapEditor | null>(null);

  useEffect(() => {
    if (!editorContainerRef.current) return;

    let instance: TiptapEditor;
    instance = new TiptapEditor({
      element: editorContainerRef.current,
      extensions: [
        StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
        TextStyle,
        Color.configure({ types: ['textStyle'] }),
        LineHeight,
        Equation,
        CharacterCount,
      ],
      content: props.initialData || '',
      editorProps: {
        attributes: {
          'aria-label': 'Essay response',
          class: 'editor-input',
        },
        handleDoubleClickOn: (_view, _pos, node, nodePos) => {
          if (node.type.name !== 'equation') return false;
          setEquationEditTarget({ latex: node.attrs.latex, position: nodePos });
          setEquationEditorIsOpen(true);
          return true;
        },
        handleKeyDown: (_view, event) => {
          if (event.key !== 'Tab') return false;
          event.preventDefault();
          instance.commands.insertContent('\t');
          return true;
        },
      },
      onUpdate: ({ editor: updatedEditor }) => {
        try {
          const content = serializeEditor(updatedEditor);
          props.onChange(content);
          autosave(content);
          rerenderToolbar(value => value + 1);
        } catch (error) {
          if (window['__airbrakeClient']) {
            window['__airbrakeClient'].notify({ error, params: { context: 'In Tiptap onUpdate' } });
          }
          throw error;
        }
      },
      onSelectionUpdate: () => rerenderToolbar(value => value + 1),
    });

    setEditor(instance);
    return () => instance.destroy();
  }, [props.id]);

  useImperativeHandle(handleRef, () => ({
    getCurrentContent: () => editor ? serializeEditor(editor) : undefined,
  }), [editor]);

  const words = wordCount(editor);

  return (
    <div className="editor-container">
      {editor && <ToolbarPlugin
        editor={editor}
        type={props.type}
        showEquationEditorButton={props.showEquationEditorButton}
        equationEditorContainerElement={props.equationEditorContainerElement}
        equationEditTarget={equationEditTarget}
        equationEditorIsOpen={equationEditorIsOpen}
        setEquationEditTarget={setEquationEditTarget}
        setEquationEditorIsOpen={setEquationEditorIsOpen}
      />}
      <div className="editor-inner">
        {!editor?.getText() && <div className="editor-placeholder">{placeholder}</div>}
        <div ref={editorContainerRef} />
      </div>
      <div className="editor-word-count" aria-live="polite">
        {words} {words === 1 ? 'word' : 'words'}
      </div>
    </div>
  );
});
